import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faSearch,
  faFilterList,
} from "@fortawesome/pro-regular-svg-icons";
import { Link, useParams } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import InputField from "../../components/InputField";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import Breadcrumbs from "../../components/Breadcrumbs";
import {
  getAddOnServicesByAgreement,
  updateBillingGroupOnAddOnService,
} from "../../api/AddOnServices";
import { getBillingGroupsByAgreement } from "../../api/BillingGroups";
import TableView from "../../components/TableView";
import Modal from "../../components/Modal";
import Dropdown from "../../components/Dropdown";
import PriceFormatter from "../../helpers/PriceFormatter";

const AddOnServicePage = () => {
  const { agreementPublicId } = useParams();
  const { getAccessToken } = useAuth();
  const [addOnService, setAddOnService] = useState([]);
  const [selectedAddOnService, setSelectedAddOnService] = useState([]);
  const [filteredAddOnService, setFilteredAddOnService] = useState([]);
  const [selectedBillingGroup, setSelectedBillingGroup] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [billingGroups, setBillingGroups] = useState([]);

  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [search, setSearch] = useState("");
  const [addOnServiceLoaded, setAddOnServiceLoaded] = useState(false);
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    confirm: () => {},
  });

  useEffect(() => {

    handleGetAddOnServiceByAgreement();
  }, []);

  const handleOpenModal = () => {
    if (selectedAddOnService.length === 0) {
      toast.error("No AddOnService selected");
      return;
    }
    setIsModalOpen(true);
  };

  const handleChangeBillingGroup = async () => {
    if (!selectedBillingGroup || selectedAddOnService.length === 0) {
      toast.error("Please select AddOnService and a billing group.");
      return;
    }

    try {
      const accessToken = await getAccessToken();

      await Promise.all(
        selectedAddOnService.map(async (addOnServicePublicId) => {
          const addOnServiceUpdateData = {
            billingGroupPublicId: selectedBillingGroup,
            addOnServicePublicId: addOnServicePublicId,
            updatedDateTimeUTC: new Date().toISOString(),
          };

          return updateBillingGroupOnAddOnService(addOnServiceUpdateData, accessToken);
        })
      );

      toast.success(
        "Billing group updated successfully for selected Add-on service!",
        {
          iconTheme: {
            primary: "#4CAF50",
            secondary: "#DFF2BF",
          },
        }
      );

      handleGetAddOnServiceByAgreement();
      setSelectedAddOnService([]);
      setIsModalOpen(false);
    } catch (error) {
      console.error("Failed to update billing group:", error);
      toast.error("Failed to update billing group for selected Add-on service.");
    }
  };

  useEffect(() => {
    const applyFilterAndSort = () => {
      let filtered = addOnService;

      if (search) {
        const searchLower = search.toLowerCase();
        filtered = addOnService.filter(
          (addOnService) =>
            addOnService.addOnServicePublicId?.toLowerCase().includes(searchLower) ||
          addOnService.name?.toLowerCase().includes(searchLower) ||
          addOnService.price?.toLowerCase().includes(searchLower) ||
          addOnService.updatedBy?.toLowerCase().includes(searchLower) ||
          addOnService.createdBy?.toLowerCase().includes(searchLower)
        );
      }

      const sorted = [...filtered].sort((a, b) => {
        const aValue = a[sortColumn]?.toString().toLowerCase();
        const bValue = b[sortColumn]?.toString().toLowerCase();
        return sortOrder === "asc"
          ? aValue > bValue
            ? 1
            : -1
          : aValue < bValue
          ? 1
          : -1;
      });

      setFilteredAddOnService(sorted);
    };

    applyFilterAndSort();
  }, [search, addOnService, sortColumn, sortOrder]);

  const handleGetAddOnServiceByAgreement = async () => {
    try {
      const accessToken = await getAccessToken();
      const newAddOnService = await getAddOnServicesByAgreement(
        agreementPublicId,
        accessToken
      );
      newAddOnService.forEach(service => {
        service.price = PriceFormatter(service.price);
      });



      setAddOnService(newAddOnService);
      setFilteredAddOnService(newAddOnService);
      setAddOnServiceLoaded(true);
    } catch (error) {
      console.error(error);
      setAddOnServiceLoaded(true);
    }
  };

  useEffect(() => {
    const fetchBillingGroups = async () => {
      try {
        const accessToken = await getAccessToken();
        const groups = await getBillingGroupsByAgreement(
          agreementPublicId,
          accessToken
        );
        setBillingGroups(groups);
      } catch (error) {
        console.error("Failed to fetch billing groups:", error);
      }
    };

    fetchBillingGroups();
  }, [agreementPublicId, getAccessToken]);

  const handleSelectBillingGroup = (billingGroupName) => {
    setSelectedBillingGroup(
      billingGroups.find((group) => group.name === billingGroupName)
        ?.billingGroupPublicId || null
    );
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            <div className="absolute w-full pointer-events-none">
              <LoadingTablePage title={"AddOnService"} loading={!addOnServiceLoaded} />
            </div>

            <div className={`${!addOnServiceLoaded ? "loading" : ""} page-content`}>
              <Breadcrumbs /> {/* Add Breadcrumbs component */}
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">Add-on services</h2>
                <Link
                  to="/product/createproduct"
                  className="btn-primary hidden"
                >
                  {" "}
                  {/* DOLD */}
                  <FontAwesomeIcon
                    icon={faPlus}
                    className="mr-2 text-lg"
                  />{" "}
                  Create Add-on service
                </Link>
              </div>
              <div className="rounded-md shadow-md border w-full p-4">
                <div className="flex mb-2 gap-4 justify-between">
                  <button
                    className="btn btn-outline-primary-small"
                    onClick={handleOpenModal}
                    disabled={selectedAddOnService.length === 0}
                  >
                    Update billing group
                  </button>
                  <div className="flex">
                    <button className="btn-outline-primary icon hidden">
                      {" "}
                      {/* DOLD */}
                      <FontAwesomeIcon
                        icon={faFilterList}
                        className="mr-2 text-lg"
                      />
                      Filter
                    </button>
                  </div>
                  <div className="flex items-center">
                    <InputField
                      iconLeft={false}
                      icon={faSearch}
                      placeholder={"name, account type"}
                      value={search}
                      onChange={(v) => {
                        setSearch(v.target.value);
                      }}
                    />
                  </div>
                </div>

                <div className="overflow-x-auto pb-[250px] mb-[-250px]">
                  <TableView
                    data={filteredAddOnService}
                    selectedRows={selectedAddOnService}
                    setSelectedRows={setSelectedAddOnService}
                    columns={[
                      { key: "name", label: "Name" },
                      { key: "price", label: "Price" },
                      { key: "billingGroupName", label: "Billing group" },
                      { key: "comment", label: "Comment" },
                    ]}
                    primaryKey="addOnServicePublicId"
                    basePath={`/${agreementPublicId}/add-on-services`}
                  />
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      <Modal isOpen={isModalOpen} setIsOpen={setIsModalOpen}>
        <div className="bg-white p-6 rounded-lg w-[400px]">
          <h2 className="text-xl font-semibold mb-4">Change Billing Group</h2>

          <Dropdown
            label="Select Billing Group"
            options={billingGroups.map((group) => group.name)}
            value={
              billingGroups.find(
                (group) => group.billingGroupPublicId === selectedBillingGroup
              )?.name || ""
            }
            onSelect={handleSelectBillingGroup}
          />

          <div className="flex justify-end mt-4 gap-4">
            <button
              className="btn-outline-primary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </button>
            <button className="btn-primary" onClick={handleChangeBillingGroup}>
              Confirm
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default AddOnServicePage;
